// Lightweight care-seeker intake / pre-registration form.
// Captures core profile context once and generates a stable masked ID
// (VEYK-CS-XXXXXXXX). Reused by Veyonika Summary Section-1 prefill.
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { ShieldCheck, Loader2, CheckCircle } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const FIELD_BASE =
  'w-full px-4 py-3 rounded-xl border border-[#E2E8F0] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 text-base bg-white';

export default function SeekerIntakePage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [options, setOptions] = useState({
    age_bands: ['<18', '18-24', '25-34', '35-44', '45-54', '55+'],
    genders: ['female', 'male', 'non-binary', 'prefer not to say', 'self-describe'],
    hormonal_stages: ['adolescent', 'reproductive', 'perimenopause', 'menopause', 'postmenopause', 'unsure'],
  });

  const [form, setForm] = useState({
    full_name: '',
    mobile: '',
    email: '',
    age_band: '',
    gender: 'female',
    hormonal_stage: '',
    city: '',
    consent: false,
    presenting_concern: '',
  });

  const [existing, setExisting] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API}/seeker/intake-options`).catch(() => null),
      axios.get(`${API}/seeker/intake`, { headers }).catch((e) => {
        if (e.response?.status === 404) return null;
        throw e;
      }),
    ])
      .then(([optsRes, intakeRes]) => {
        if (optsRes?.data) setOptions(optsRes.data);
        if (intakeRes?.data) {
          setExisting(intakeRes.data);
          setForm({
            full_name: intakeRes.data.full_name || '',
            mobile: intakeRes.data.mobile || '',
            email: intakeRes.data.email || '',
            age_band: intakeRes.data.age_band || '',
            gender: intakeRes.data.gender || 'female',
            hormonal_stage: intakeRes.data.hormonal_stage || '',
            city: intakeRes.data.city || '',
            consent: !!intakeRes.data.consent,
            presenting_concern: intakeRes.data.presenting_concern || '',
          });
        } else if (user) {
          setForm((f) => ({
            ...f,
            full_name: f.full_name || user.name || '',
            email: f.email || user.email || '',
            mobile: f.mobile || user.phone || '',
          }));
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Please enter your full name';
    if (!/^[+0-9\s\-()]{6,20}$/.test(form.mobile.trim())) e.mobile = 'Enter a valid mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.age_band) e.age_band = 'Select an age band';
    if (!form.gender) e.gender = 'Select an option';
    if (!form.hormonal_stage) e.hormonal_stage = 'Select your current life-stage';
    if (!form.city.trim()) e.city = 'Please enter your city';
    if (!form.consent) e.consent = 'We need your consent to continue';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/seeker/intake`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setExisting(res.data);
      setDirty(false);
      // Best-effort: also record the consent in the audit log (terms + privacy
      // versions are fetched from /api/policies/versions; silent-fail if
      // anything goes wrong — intake itself is the source of truth).
      try {
        const pol = await axios.get(`${API}/policies/versions`);
        const headers = { Authorization: `Bearer ${token}` };
        await Promise.all([
          axios.post(`${API}/consents`, {
            consent_type: 'intake',
            version: pol.data.terms_version,
            accepted: true,
            context: 'seeker_intake',
          }, { headers }),
          axios.post(`${API}/consents`, {
            consent_type: 'privacy',
            version: pol.data.privacy_version,
            accepted: true,
            context: 'seeker_intake',
          }, { headers }),
        ]);
      } catch (_) { /* non-blocking */ }
      toast.success(existing ? 'Intake updated' : `Welcome! Your ID: ${res.data.masked_id}`);
      // Brief pause so the toast is visible, then return to dashboard.
      setTimeout(() => navigate('/seeker-dashboard'), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save intake');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="seeker-intake-page">
      <AppHeader
        title={existing ? 'Update Your Intake' : 'Welcome'}
        hasUnsavedChanges={dirty}
        onBack={() => navigate('/seeker-dashboard')}
      />

      <div className="container mx-auto px-6 py-10 max-w-2xl">
        {existing && existing.masked_id && (
          <div
            className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-pink-50 to-purple-50 border border-purple-100 flex items-center gap-3"
            data-testid="intake-masked-id-banner"
          >
            <CheckCircle className="text-purple-600 shrink-0" size={22} />
            <div className="text-sm">
              <div className="text-gray-600">Your ID</div>
              <div className="font-mono font-semibold text-purple-700">{existing.masked_id}</div>
            </div>
          </div>
        )}

        <p className="text-[#718096] mb-8 text-sm">
          A few quick details help our care team understand your context before your first session. You can
          update these any time from your dashboard.
        </p>

        <form onSubmit={submit} className="space-y-5" noValidate>
          {/* Identity */}
          <Field label="Full name" error={errors.full_name} required>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              className={FIELD_BASE}
              placeholder="As you'd like the counsellor to address you"
              data-testid="intake-full-name"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Mobile number" error={errors.mobile} required>
              <input
                type="tel"
                value={form.mobile}
                onChange={(e) => handleChange('mobile', e.target.value)}
                className={FIELD_BASE}
                placeholder="+91 ..."
                data-testid="intake-mobile"
              />
            </Field>
            <Field label="Email" error={errors.email} required>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={FIELD_BASE}
                placeholder="you@example.com"
                data-testid="intake-email"
              />
            </Field>
          </div>

          {/* Context */}
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Age band" error={errors.age_band} required>
              <select
                value={form.age_band}
                onChange={(e) => handleChange('age_band', e.target.value)}
                className={FIELD_BASE}
                data-testid="intake-age-band"
              >
                <option value="">— Select —</option>
                {options.age_bands.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </Field>
            <Field label="Birth gender" error={errors.gender} required>
              <select
                value={form.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                className={FIELD_BASE}
                data-testid="intake-gender"
              >
                {options.genders.map((g) => (
                  <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Current hormonal life-stage" error={errors.hormonal_stage} required>
              <select
                value={form.hormonal_stage}
                onChange={(e) => handleChange('hormonal_stage', e.target.value)}
                className={FIELD_BASE}
                data-testid="intake-hormonal-stage"
              >
                <option value="">— Select —</option>
                {options.hormonal_stages.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </Field>
            <Field label="City" error={errors.city} required>
              <input
                type="text"
                value={form.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className={FIELD_BASE}
                placeholder="Bengaluru, Mumbai…"
                data-testid="intake-city"
              />
            </Field>
          </div>

          <Field
            label="What brings you here today?"
            hint="Optional — a sentence or two so the counsellor can prepare"
          >
            <textarea
              value={form.presenting_concern}
              onChange={(e) => handleChange('presenting_concern', e.target.value)}
              className={FIELD_BASE}
              rows={3}
              maxLength={500}
              placeholder="e.g. Anxiety around perimenopausal symptoms, irregular cycles…"
              data-testid="intake-concern"
            />
          </Field>

          {/* Consent */}
          <label
            className="flex items-start gap-3 p-4 rounded-2xl border border-[#E2E8F0] bg-white cursor-pointer"
            data-testid="intake-consent-row"
          >
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => handleChange('consent', e.target.checked)}
              className="mt-1 w-5 h-5 accent-primary"
              data-testid="intake-consent-checkbox"
            />
            <span className="text-sm text-[#2D3748]">
              <span className="inline-flex items-center gap-1 font-medium text-primary">
                <ShieldCheck size={16} /> Consent
              </span>
              <span className="block mt-1 text-[#718096]">
                I agree that the service may store the information above to provide care and may share it with
                the counsellor assigned to me. I understand my data is handled per the privacy policy.
              </span>
              {errors.consent && (
                <span className="block mt-1 text-xs text-red-600">{errors.consent}</span>
              )}
            </span>
          </label>

          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={() => navigate('/seeker-dashboard')}
              className="text-sm text-[#718096] hover:text-primary"
              data-testid="intake-skip-btn"
            >
              Skip for now
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary px-8 py-3 flex items-center gap-2 disabled:opacity-60"
              data-testid="intake-submit-btn"
            >
              {submitting && <Loader2 size={18} className="animate-spin" />}
              {existing ? 'Save changes' : 'Complete intake'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, error, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#2D3748] mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-[#A0AEC0]">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
